import styled from 'styled-components';
import { palette } from 'styled-theme';

// Main container
export const Container = styled.div`
  padding: 24px;
  background: #f9fafb;
  min-height: 100vh;
  margin-bottom: 20px;
  border: 1px solid ${palette('border', 1)};
  background: transparent;
  border-radius: 12px;
`;

// Header section
export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding: 20px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

export const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
`;

export const SaveButton = styled.button`
  padding: 10px 24px;
  background: linear-gradient(90deg, #7b5cff, #60a5fa);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Hidden textarea for JSON
export const HiddenJsonArea = styled.textarea`
  display: none;
`;

// Question tree container
export const QuestionTreeContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

// Question node
export const QuestionNode = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  background: #f9fafb;
  border-left: 4px solid #3b82f6;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

export const QuestionNodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const ToggleButton = styled.button`
  width: 28px;
  height: 28px;
  padding: 4px;
  background: #e5e7eb;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #d1d5db;
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
    transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

export const QuestionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  flex: 1;
`;

export const ActionButtonsGroup = styled.div`
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${QuestionNode}:hover & {
    opacity: 1;
  }
`;

export const ActionButton = styled.button`
  width: 28px;
  height: 28px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const EditButton = styled(ActionButton)`
  background: #dbeafe;
  color: #0284c7;

  &:hover {
    background: #bfdbfe;
  }
`;

export const AddOptionButton = styled(ActionButton)`
  background: #dcfce7;
  color: #16a34a;

  &:hover {
    background: #bbf7d0;
  }
`;

export const DeleteButton = styled(ActionButton)`
  background: #fee2e2;
  color: #dc2626;

  &:hover {
    background: #fecaca;
  }
`;

export const QuestionContent = styled.div`
  margin-top: 12px;
`;

export const QuestionText = styled.p`
  font-size: 14px;
  color: #4b5563;
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

export const OptionItem = styled.div`
  border-left: 3px solid #3b82f6;
  background: #ffffff;
  padding: 12px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }
`;

export const OptionText = styled.p`
  font-size: 13px;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  font-weight: 500;
`;

export const ClusterBadge = styled.div`
  display: inline-block;
  background: #dbeafe;
  color: #0284c7;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
`;

export const NestedQuestionsContainer = styled.div`
  margin-left: 24px;
  margin-top: 12px;
  padding-left: 12px;
  border-left: 2px solid #e5e7eb;
`;

// Modal styles
export const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 32px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 24px 0;
`;

export const FormGroup = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
`;

export const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

export const FormInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;
  background: #f9fafb;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

export const FormTextarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  background: #f9fafb;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

export const FormSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

export const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
`;

export const ModalButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryModalButton = styled(ModalButton)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #ffffff;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
`;

export const SecondaryModalButton = styled(ModalButton)`
  background: #e5e7eb;
  color: #1a1a1a;

  &:hover:not(:disabled) {
    background: #d1d5db;
  }
`;

// Loading and error states
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

export const ErrorContainer = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 16px;
  color: #991b1b;
  font-size: 14px;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 14px;
`;

// Add input for options
export const AddInputContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

export const AddInput = styled(FormInput)`
  flex: 1;
  margin-bottom: 0;
`;

export const AddInputButton = styled(ActionButton)`
  background: #dcfce7;
  color: #16a34a;
  padding: 10px;
  width: auto;

  &:hover {
    background: #bbf7d0;
  }
`;

// Validation prompt conditional
export const ConditionalFieldWrapper = styled.div`
  display: ${(props) => (props.visible ? 'block' : 'none')};
`;
